// src/services/stockReportService.js
import { loadLineChunkRowsForDateRange } from "../features/salesDataImport/services/salesLineChunksReader";
import { rowMatchesDateRange } from "../features/salesDataImport/utils/dateParse";
import {
  importRowBrandNameField,
  invoiceTypeFromImportRow,
} from "./salesImportTransactionDataService";

const stockReportCache = new Map();
const STOCK_REPORT_CACHE_MAX = 12;

function cacheStockReport(key, list) {
  if (stockReportCache.size >= STOCK_REPORT_CACHE_MAX) {
    const first = stockReportCache.keys().next().value;
    stockReportCache.delete(first);
  }
  stockReportCache.set(key, list);
}

export function clearStockReportCache() {
  stockReportCache.clear();
}

/** Single-pass stock aggregation from import line rows (no invoice grouping). */
function buildStockMapFromImportRows(rows, fromDate = "", toDate = "") {
  const map = Object.create(null);

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    if (fromDate || toDate) {
      if (!rowMatchesDateRange(row, fromDate, toDate)) continue;
    }

    const type = invoiceTypeFromImportRow(row);
    if (type === "transfer") continue;

    const code = String(row.partNo || row.code || "").trim();
    if (!code) continue;

    const qty = Number(row.qty) || 0;
    if (!qty) continue;

    const rate = Number(row.unitPrice) || Number(row.rate) || 0;
    const brand =
      importRowBrandNameField(row) || String(row.itemGroup || row.itemgroup || "").trim();

    let entry = map[code];
    if (!entry) {
      entry = {
        code,
        desc: String(row.itemName || row.desc || "-").trim() || "-",
        group: String(row.itemGroup || row.itemgroup || row.group || "-").trim() || "-",
        brand: brand || "-",
        unit: String(row.unit || "Nos").trim() || "Nos",
        qty: 0,
        rate: 0,
        value: 0,
        gst: row.gst ?? "-",
      };
      map[code] = entry;
    }

    if (rate > 0) entry.rate = rate;
    if (type === "inward" || type === "creditnote") {
      entry.qty += qty;
    } else {
      entry.qty -= qty;
    }
  }

  return map;
}

function mapToStockList(map) {
  const list = Object.values(map).map((r) => {
    const qty = Number(r.qty) || 0;
    const rate = Number(r.rate) || 0;
    return {
      ...r,
      qty,
      rate,
      value: Number((qty * rate).toFixed(2)),
    };
  });
  list.sort((a, b) => (a.code || "").localeCompare(b.code || ""));
  return list;
}

/**
 * Net stock by part from uploaded sales import (inward + credit note add, sales deduct).
 * @param {string|null} fromDate YYYY-MM-DD
 * @param {string|null} toDate YYYY-MM-DD
 */
export async function getStockReport(fromDate, toDate) {
  const fromStr = fromDate ? String(fromDate).slice(0, 10) : "";
  const toStr = toDate ? String(toDate).slice(0, 10) : "";
  const cacheKey = `${fromStr}|${toStr}`;

  if (stockReportCache.has(cacheKey)) {
    return stockReportCache.get(cacheKey);
  }

  const { rows } = await loadLineChunkRowsForDateRange({ fromDate: fromStr, toDate: toStr });
  const list = mapToStockList(buildStockMapFromImportRows(rows, fromStr, toStr));
  cacheStockReport(cacheKey, list);
  return list;
}
